// types/cart.ts - Updated with proper variant handling

// Base discount structure
export interface DiscountInfo {
  isActive: boolean;
  type: string;
  value: number;
  startDate: string;
  endDate: string;
  couponName: string;
  discountedPrice: number;
}

// Image structure
export interface ProductImage {
  _id: string;
  url?: string;
  data?: string;
  contentType?: string;
  resizedImages: Array<{
    _id: string;
    id: string;
    width: number;
    url: string;
  }>;
}

// Sub-sub-variant structure (third level)
export interface SubSubVariant {
  _id: string;
  name: string;
  value: string;
  price: number;
  stock?: number;
  totalStock?: number;
  images: ProductImage[];
}

// Sub-variant structure (second level)
export interface SubVariant {
  _id: string;
  name: string;
  value: string;
  price: number;
  stock?: number;
  totalStock?: number;
  images: ProductImage[];
  subSubVariants: SubSubVariant[];
}

// Main variant structure (first level)
export interface ProductVariant {
  _id: string;
  name: string;
  value: string;
  price: number;
  discount: DiscountInfo;
  totalStock: number;
  images: ProductImage[];
  subVariants: SubVariant[];
}

// Selected variant path for cart items - tracks the complete selection hierarchy
export interface SelectedVariantPath {
  // Level 1 - Always present when variants exist
  variantId: string;
  variantName: string;
  variantValue: string;
  
  // Level 2 - Optional second level
  subVariantId?: string;
  subVariantName?: string;
  subVariantValue?: string;
  
  // Level 3 - Optional third level
  subSubVariantId?: string;
  subSubVariantName?: string;
  subSubVariantValue?: string;
}

// Cart item structure
export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  price: number;
  originalPrice: number;
  discountPercentage: number;
  discount?: DiscountInfo;
  
  // Variant tracking via path (IDs and values)
  selectedVariantPath?: SelectedVariantPath;
  
  // Display text for selected variants (e.g., "Color: White, Size: Large")
  variantDisplayText?: string;
  
  quantity: number;
  totalPrice: number;
  
  sellerId: string;
  sellerName: string;
  weight: number;
}

// Product with full variant structure
export interface ProductWithVariants {
  _id: string;
  id?: string;
  name: string;
  description: string;
  price: number;
  discount?: DiscountInfo;
  discountedPrice?: number;
  originalPrice?: number;
  discountPercentage?: number;
  
  category: string;
  subCategory: string;
  brand: string;
  
  images: ProductImage[];
  variants: ProductVariant[];
  
  stock: number;
  lowStockAlert: number;
  sku: string;
  upc: string;
  condition: string;
  
  weight?: number;
  minimumPurchaseQuantity: number;
  
  seller: any; // Use your existing SellerInfo type
  
  shippingInfo?: {
    weight: {
      value: number;
      unit: string;
    };
    dimensions: {
      length: number;
      width: number;
      height: number;
    };
  };
  
  returnPolicy?: {
    allowed: boolean;
    periodInDays: number;
    conditions: any[];
  };
  
  pricing?: {
    taxable: boolean;
    taxRate: number;
    originalPrice: number;
    discountInfo: DiscountInfo;
  };
  
  visibility: string;
  status: string;
}

export enum ModalTriggerSource {
  LISTING = 'listing',
  DETAIL_PAGE = 'detail_page',
  CART = 'cart',
  BLIP_FEED = 'blip_feed',
}

// Helper function to check if variant path is complete
export function isVariantPathComplete(
  product: ProductWithVariants,
  selectedPath?: SelectedVariantPath
): boolean {
  if (!selectedPath) return false;
  
  const variant = product.variants.find(v => v._id === selectedPath.variantId);
  if (!variant) return false;
  
  // Check if we need sub-variants
  if (variant.subVariants && variant.subVariants.length > 0) {
    if (!selectedPath.subVariantId) return false;
    
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    if (!subVariant) return false;
    
    // Check if we need sub-sub-variants
    if (subVariant.subSubVariants && subVariant.subSubVariants.length > 0) {
      return !!selectedPath.subSubVariantId;
    }
  }
  
  return true;
}

// Helper function to calculate effective price based on variant selection
export function getEffectivePrice(
  product: ProductWithVariants,
  selectedPath?: SelectedVariantPath
): number {
  if (!selectedPath) {
    return product.discountedPrice || product.discount?.discountedPrice || product.price;
  }

  const variant = product.variants.find(v => v._id === selectedPath.variantId);
  if (!variant) return product.price;

  // Check deepest level first
  if (selectedPath.subSubVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    const subSubVariant = subVariant?.subSubVariants.find(ssv => ssv._id === selectedPath.subSubVariantId);
    return subSubVariant?.price || subVariant?.price || variant.price;
  }

  if (selectedPath.subVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    return subVariant?.price || variant.price;
  }

  return variant.price;
}

// Helper function to get available stock for selected variant path
export function getAvailableStock(
  product: ProductWithVariants,
  selectedPath?: SelectedVariantPath
): number {
  if (!selectedPath) {
    return product.stock;
  }

  const variant = product.variants.find(v => v._id === selectedPath.variantId);
  if (!variant) return 0;

  if (selectedPath.subSubVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    const subSubVariant = subVariant?.subSubVariants.find(ssv => ssv._id === selectedPath.subSubVariantId);
    return subSubVariant?.stock || subSubVariant?.totalStock || 0;
  }

  if (selectedPath.subVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    return subVariant?.stock || subVariant?.totalStock || 0;
  }

  return variant.totalStock;
}

// Helper to generate display text for selected variants
export function generateVariantDisplayText(selectedPath?: SelectedVariantPath): string {
  if (!selectedPath) return '';
  
  const parts: string[] = [];
  
  if (selectedPath.variantName && selectedPath.variantValue) {
    parts.push(`${selectedPath.variantName}: ${selectedPath.variantValue}`);
  }
  
  if (selectedPath.subVariantName && selectedPath.subVariantValue) {
    parts.push(`${selectedPath.subVariantName}: ${selectedPath.subVariantValue}`);
  }
  
  if (selectedPath.subSubVariantName && selectedPath.subSubVariantValue) {
    parts.push(`${selectedPath.subSubVariantName}: ${selectedPath.subSubVariantValue}`);
  }
  
  return parts.join(', ');
}

// Helper to get the primary image for selected variant path
export function getVariantImage(
  product: ProductWithVariants,
  selectedPath?: SelectedVariantPath
): string {
  if (!selectedPath) {
    return product.images?.[0]?.url || product.images?.[0]?.data || '';
  }

  const variant = product.variants.find(v => v._id === selectedPath.variantId);
  if (!variant) return product.images?.[0]?.url || product.images?.[0]?.data || '';

  // Get image from deepest selected level
  if (selectedPath.subSubVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    const subSubVariant = subVariant?.subSubVariants.find(ssv => ssv._id === selectedPath.subSubVariantId);
    const image = subSubVariant?.images?.[0]?.url || subSubVariant?.images?.[0]?.data;
    if (image) return image;
  }

  if (selectedPath.subVariantId) {
    const subVariant = variant.subVariants.find(sv => sv._id === selectedPath.subVariantId);
    const image = subVariant?.images?.[0]?.url || subVariant?.images?.[0]?.data;
    if (image) return image;
  }

  const image = variant.images?.[0]?.url || variant.images?.[0]?.data;
  if (image) return image;

  return product.images?.[0]?.url || product.images?.[0]?.data || '';
}

// Helper to compare variant paths
export function isSameVariantPath(
  path1?: SelectedVariantPath,
  path2?: SelectedVariantPath
): boolean {
  if (!path1 && !path2) return true;
  if (!path1 || !path2) return false;
  
  return (
    path1.variantId === path2.variantId &&
    path1.subVariantId === path2.subVariantId &&
    path1.subSubVariantId === path2.subSubVariantId
  );
}